import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API_BASE = `${BACKEND_URL}/api/station-logbook`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors and verify module header
api.interceptors.response.use(
  (response) => {
    // Verify X-Module header in development
    if (process.env.NODE_ENV === "development") {
      const moduleHeader = response.headers?.["x-module"];
      if (moduleHeader && moduleHeader !== "station-logbook") {
        console.error(
          "WRONG MODULE RESPONSE! Expected station-logbook but got:",
          moduleHeader,
          "URL:",
          response.config?.url
        );
      }
    }
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      // Use basename-aware redirect for production
      const hostname = window.location.hostname;
      const loginPath = hostname.includes('stationlogbook.com') ? '/station-logbook/login' : '/login';
      window.location.href = loginPath;
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  validateSsoToken: (token) => api.post(`/auth/sso-validate?token=${token}`),
};

// Users API
export const usersAPI = {
  getAll: (params) => api.get("/users", { params }),
  update: (userId, data) => api.put(`/users/${userId}`, data),
  bulkUpdateOffice: (data) => api.post("/users/bulk-update-office", data),
};

// Stations API (Admin-Managed)
export const stationsAPI = {
  getAll: (params) => api.get("/stations", { params }),
  getById: (id) => api.get(`/stations/${id}`),
  getByCode: async (code) => (await api.get(`/stations/by-code/${code}`)).data,
  search: async (q) => (await api.get("/stations/search", { params: { q } })).data,
  create: (data) => api.post("/stations", data),
  update: (id, data) => api.put(`/stations/${id}`, data),
  deactivate: (id) => api.post(`/stations/${id}/deactivate`),
  reactivate: (id) => api.post(`/stations/${id}/reactivate`),
  importCSV: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/import/stations", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
};

// Office Locations API
export const officeLocationsAPI = {
  getAll: (params) => api.get("/office-locations", { params }),
  getById: (id) => api.get(`/office-locations/${id}`),
  create: (data) => api.post("/office-locations", data),
  update: (id, data) => api.put(`/office-locations/${id}`, data),
  deactivate: (id) => api.post(`/office-locations/${id}/deactivate`),
  reactivate: (id) => api.post(`/office-locations/${id}/reactivate`),
};

// Equipment API
export const equipmentAPI = {
  getAll: (stationId) =>
    api.get("/equipment", { params: stationId ? { station_id: stationId } : {} }),
  getById: (id) => api.get(`/equipment/${id}`),
  create: (data) => api.post("/equipment", data),
};

// Departments API
export const departmentsAPI = {
  getAll: () => api.get("/departments"),
  create: (data) => api.post("/departments", data),
};

// Visits API
export const visitsAPI = {
  entry: (data) => api.post("/visits/entry", data),
  exit: (visitId, data) => api.post(`/visits/${visitId}/exit`, data),
  getActive: () => api.get("/visits/active"),
  getAllActive: () => api.get("/visits/active/all"),
  getIncomplete: () => api.get("/visits/incomplete"),
  getHistory: (params) => api.get("/visits/history", { params }),
  getById: (id) => api.get(`/visits/${id}`),
};

// Activities API
export const activitiesAPI = {
  create: (data) => api.post("/activities", data),
  getByVisit: (visitId) => api.get("/activities", { params: { visit_id: visitId } }),
};

// Audit Logs API
export const auditAPI = {
  getLogs: (params) => api.get("/audit-logs", { params }),
};

// Reports API
export const reportsAPI = {
  getActivePersonnel: (params) => api.get("/reports/active-personnel", { params }),
  getStationSummary: (stationId, days = 30) =>
    api.get(`/reports/station-summary/${stationId}`, { params: { days } }),
};

// Hub Modules API
export const modulesAPI = {
  getAll: () => api.get("/modules"),
  getAllAdmin: () => api.get("/modules/all"),
  create: (data) => api.post("/modules", data),
  update: (id, data) => api.put(`/modules/${id}`, data),
  getSsoLink: (moduleId) => api.post(`/modules/${moduleId}/sso-link`),
};

// SMS API
export const smsAPI = {
  send: (data) => api.post("/sms/send", data),
};

// Seed API
export const seedAPI = {
  seed: () => api.post("/seed"),
};

export default api;
